import "../src/styling/constants/typography.css";
import "./app.css";

import { useEffect } from "react";
import { useAuth, useSession } from "@clerk/clerk-react";

import { useAppState } from "hook/useAppState";
import { useUserData } from "hook/useUserData";
import { useSubscription } from "hook/useSubscription";

import ShowQuotaAdditionInProgress from "components/popUps/showQuotaAdditionInProgress";
import SuccessToast from "components/popUps/SuccessToast";
import CreateSubscription from "components/subscription/create-subscription/createSubscription";

import { AppRouter } from "routes/appRouter";
import HarbourClubPromotion from "components/harbour-club-promotion/harbourClubPromotion";
import { setTokenGetter } from "axiosWithToken";
import { persistor } from "components/common/store/store";
import DomainSettingsDialog from "components/domain-settings/domainSettings";
import { ORGANIZATION_ROLE } from "layout/sidebar/types";
import Loader from "components/common/loader";
import { SUBSCRIPTION_DISPLAY_NAME } from "components/subscription/types";

function App() {
  const { isSignedIn } = useAuth();
  const { session } = useSession();

  const {
    showSuccessToast,
    succesText,
    isLoading,
    showProcessingModal,
    credits,
    setCredits,
    successClicked,
    syncFirebaseAuth,
  } = useAppState();

  const { userData, setUserData, loadReducedUserFromDB } = useUserData();

  const {
    isSubscriptionLoading,
    newPlans,
    createNewSubscription,
    showDomainSettingsModal,
    currentSubscription,
    setShowDomainSettingsModal,
    setCreateNewSubscription,
    handleSubscriptionChoosen,
    handleCalendlyInvite,
    fetchCurrentSubscription,
  } = useSubscription();

  setTokenGetter(async () => {
    const token = await session?.getToken({
      template: "bizcrunch-organization",
    });
    return token ?? null;
  });

  useEffect(() => {
    const fetchData = async () => {
      if (isSignedIn) {
        await syncFirebaseAuth();
        await fetchCurrentSubscription(false);
        await loadReducedUserFromDB();
      } else {
        localStorage.clear();
        persistor.purge();
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  const handlePurchasePlan = (plan: any, isFreeTrial: boolean) =>
    handleSubscriptionChoosen(plan, userData);

  const handleInvite = () => handleCalendlyInvite(userData);

  const bgImage =
    userData?.isHarbourClub || userData?.isBiz4Biz
      ? "/assets/bottomGraphicHC.png"
      : "/assets/bottomGraphic.png";

  const userDetails = JSON.parse(localStorage.getItem("user") || "{}");
  const urlParams = new URLSearchParams(window.location.search);

  const isStillRedirectingFromCheckout =
    urlParams.get("redirectFromCheckout") === "true";

  return (
    <>
      {isSignedIn &&
        (isLoading || isSubscriptionLoading || !userData) &&
        !showProcessingModal && <Loader />}

      <div
        className="App"
        style={{
          backgroundImage: `url(${bgImage})`,
          position: "relative",
          bottom: 0,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom",
          backgroundSize: "contain",
        }}
      >
        {showProcessingModal && (
          <ShowQuotaAdditionInProgress isOpen={showProcessingModal} />
        )}

        <CreateSubscription
          plansData={newPlans}
          open={createNewSubscription}
          purchasePlan={handlePurchasePlan}
          handleCalendlyInvite={handleInvite}
          close={() => setCreateNewSubscription(false)}
        />

        {userDetails?.organizationRole === ORGANIZATION_ROLE.ADMIN &&
          currentSubscription.product.displayName !==
            SUBSCRIPTION_DISPLAY_NAME.EMERGING && !isStillRedirectingFromCheckout && (
            <DomainSettingsDialog
              open={showDomainSettingsModal}
              onOpenChange={() => setShowDomainSettingsModal(false)}
            />
          )}

        <AppRouter
          loggedIn={isSignedIn ?? false}
          userData={userData}
          setUserData={setUserData}
          credits={credits}
          updateCredits={setCredits}
          saveToast={successClicked}
        />

        {showSuccessToast && <SuccessToast text={succesText} />}

        {!isSignedIn && <HarbourClubPromotion />}
      </div>
    </>
  );
}

export default App;
